export interface SkipFeedbackPayload {
  feedback: string;
  contact_name?: string;
  contact_email?: string;
  tsp_name?: string;
}

export function isValidEmail(email: string | null | undefined): boolean {
  if (!email) {
    return false;
  }

  const trimmed = email.trim();
  if (!trimmed) {
    return false;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(trimmed);
}

export interface SkipFeedbackMeta {
  reasonValue?: SkipReasonValue | '';
  reasonLabel?: string;
  details?: string;
}

export interface SkipFeedbackBuildResult {
  payload: SkipFeedbackPayload;
  meta: SkipFeedbackMeta;
}

export const SKIP_FEEDBACK_REASONS = [
  { value: 'credentials_issue', label: "I don't have my login credentials" },
  { value: 'provider_not_listed', label: "Couldn't find my provider" },
  { value: 'will_try_later', label: "I'll connect this later" },
  { value: 'other', label: 'Other' },
] as const;

export type SkipReasonValue = (typeof SKIP_FEEDBACK_REASONS)[number]['value'];

export const PROVIDER_REASON: SkipReasonValue = 'provider_not_listed';
export const OTHER_REASON: SkipReasonValue = 'other';

interface BuildFeedbackPayloadOptions {
  reasonValue: SkipReasonValue | '';
  reasonLabel?: string;
  details?: string;
  contactRequested?: boolean;
  contactName?: string;
  contactEmail?: string;
  defaultMessage: string;
}

export function buildSkipFeedbackPayload({
  reasonValue,
  reasonLabel,
  details,
  contactRequested,
  contactName,
  contactEmail,
  defaultMessage,
}: BuildFeedbackPayloadOptions): SkipFeedbackBuildResult {
  const baseReason = reasonLabel?.trim() || '';
  const trimmedDetails = details?.trim() || '';

  const payload: SkipFeedbackPayload = {
    feedback: baseReason.length > 0 ? baseReason : defaultMessage,
  };

  if (reasonValue === PROVIDER_REASON && trimmedDetails) {
    payload.tsp_name = trimmedDetails;
  } else if (trimmedDetails) {
    payload.feedback = `${payload.feedback} - ${trimmedDetails}`.trim();
  }

  if (contactRequested) {
    const trimmedName = contactName?.trim();
    const trimmedEmail = contactEmail?.trim();

    if (trimmedName) {
      payload.contact_name = trimmedName;
    }

    if (trimmedEmail) {
      if (isValidEmail(trimmedEmail)) {
        payload.contact_email = trimmedEmail;
      } else {
        console.warn('[SkipFeedback] Ignoring invalid contact email', {
          contactEmail: trimmedEmail,
        });
      }
    }
  }

  return {
    payload,
    meta: {
      reasonValue,
      reasonLabel,
      details: trimmedDetails,
    },
  };
}

export async function submitSkipFeedback(
  token: string | null | undefined,
  payload?: SkipFeedbackPayload
): Promise<void> {
  if (!payload) {
    return;
  }

  const trimmedFeedback = payload.feedback?.trim();
  if (!trimmedFeedback) {
    return;
  }

  try {
    const { tokenStorage } = await import('./tokenStorage');

    let activeToken = token ?? null;
    if (!activeToken) {
      activeToken = tokenStorage.get();
    }

    if (!activeToken) {
      console.warn('[SkipFeedback] No token available, skipping submission');
      return;
    }

    const requestBody: SkipFeedbackPayload = {
      feedback: trimmedFeedback,
    };

    if (payload.contact_name?.trim()) {
      requestBody.contact_name = payload.contact_name.trim();
    }

    if (payload.contact_email?.trim()) {
      const trimmedEmail = payload.contact_email.trim();
      if (isValidEmail(trimmedEmail)) {
        requestBody.contact_email = trimmedEmail;
      }
    }

    if (payload.tsp_name?.trim()) {
      requestBody.tsp_name = payload.tsp_name.trim();
    }

    tokenStorage.set(activeToken);

    const axios = (await import('../axios')).default;

    await axios.post('/service/onboard/failure', requestBody, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error: any) {
    console.warn('[SkipFeedback] Failed to submit feedback', {
      message: error?.message,
      status: error?.response?.status,
    });
  }
}
